import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { AssessmentMatrixApiService, AssessmentMatrixDto } from '@formrails/form-builder/data-access';
import { DrawerResizeDirective } from '@formrails/shared/ui';

/**
 * Assigns the assessment matrices an application is assessed against.
 *
 * Self-contained on purpose: the chip, its drawer, the matrix list and the
 * selection all live here, and the only thing the form builder does is bind
 * `matrixIds` and render the element for Application templates. If matrices
 * move onto the workflow instead, deleting this folder and that one element
 * removes the feature.
 */
@Component({
  selector: 'app-assessment-matrices',
  standalone: true,
  imports: [FormsModule, DrawerResizeDirective],
  templateUrl: './assessment-matrices.component.html',
  styleUrl: './assessment-matrices.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssessmentMatricesComponent {
  /** The template's assigned matrix ids; two-way, so the builder owns the value. */
  readonly matrixIds = model<string[]>([]);
  /** A read-only form shows its matrices but can't change them. */
  readonly isReadOnly = model(false);

  private api = inject(AssessmentMatrixApiService);

  readonly drawerOpen = signal(false);
  readonly searchText = signal('');

  /** Every matrix that can be chosen. Empty while loading, or if the call fails. */
  private readonly matrices = toSignal(this.api.getMatrices(), { initialValue: [] as AssessmentMatrixDto[] });

  readonly selectedCount = computed(() => this.matrixIds().length);

  readonly chipLabel = computed(() => {
    const count = this.selectedCount();
    if (count === 0) {
      return 'Add matrices';
    }
    return count === 1 ? '1 matrix' : `${count} matrices`;
  });

  /** The chosen matrices, named, for the chip's tooltip. */
  readonly selectedNames = computed(() => {
    const ids = new Set(this.matrixIds());
    return this.matrices()
      .filter(matrix => ids.has(matrix.id))
      .map(matrix => matrix.name);
  });

  readonly filteredMatrices = computed(() => {
    const search = this.searchText().trim().toLowerCase();
    const matrices = this.matrices();
    if (!search) {
      return matrices;
    }
    return matrices.filter(
      matrix =>
        matrix.name?.toLowerCase().includes(search) ||
        matrix.description?.toLowerCase().includes(search)
    );
  });

  openDrawer(): void {
    if (this.isReadOnly()) return;
    this.searchText.set('');
    this.drawerOpen.set(true);
  }

  closeDrawer(): void {
    this.drawerOpen.set(false);
  }

  isSelected(matrix: AssessmentMatrixDto): boolean {
    return this.matrixIds().includes(matrix.id);
  }

  toggle(matrix: AssessmentMatrixDto): void {
    const ids = this.matrixIds();
    this.matrixIds.set(
      ids.includes(matrix.id) ? ids.filter(id => id !== matrix.id) : [...ids, matrix.id]
    );
  }

  clearAll(): void {
    this.matrixIds.set([]);
  }
}
